import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLanguage } from '../i18n/LanguageContext';
import { APP } from '../config/app';
import {
  createMap,
  destroyMap,
  getBestDevicePosition,
  getAccuratePosition,
  reverseGeocode,
  isWithinServiceArea,
  isBillingError,
  getGeoErrorKey,
  bindPinnedCenterPin,
  fitMapToPoint,
} from '../services/maps';
import MapRecenterIcon from './MapRecenterIcon';
import useScrollLock from '../utils/useScrollLock';
import { showLocationDeniedToast } from '../utils/locationPermission';

/**
 * Full-screen map pin picker for saving Home / Work without a past ride.
 */
export default function PlacePinPicker({ title, confirmLabel, initialPlace, pinKind = 'home', onConfirm, onClose }) {
  const { t } = useLanguage();
  useScrollLock();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const geocodeSeq = useRef(0);
  const initialRef = useRef(initialPlace);
  const [mapReady, setMapReady] = useState(false);
  const [pinPlace, setPinPlace] = useState(null);
  const [pinValid, setPinValid] = useState(true);
  const [pinLoading, setPinLoading] = useState(true);
  const [locating, setLocating] = useState(false);
  const [mapError, setMapError] = useState('');

  const pinName = pinLoading
    ? t('book.pinApprox')
    : (pinPlace?.name || pinPlace?.address || '—');
  const pinAddr = pinLoading
    ? ''
    : (pinPlace?.address && pinPlace.address !== pinPlace.name ? pinPlace.address : '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const start = initialRef.current;
        const center = start?.lat
          ? { lat: start.lat, lng: start.lng }
          : APP.mapCenter;
        // Native in the app: a full-screen overlay over Profile, so Profile
        // is hidden under it while it is open (nativeMapLayers.js).
        const map = await createMap(mapRef.current, center, {
          allowNative: true,
          nativeOverlayRoot: containerRef.current,
        });
        if (cancelled) { destroyMap(map); return; }
        mapInstance.current = map;
        fitMapToPoint(map, center, { top: 56, bottom: 220 }, 17);

        const ok = isWithinServiceArea(center.lat, center.lng);
        setPinValid(ok);
        if (start?.name || start?.address) {
          setPinPlace(start);
          setPinLoading(false);
        } else {
          setPinPlace({
            ...center,
            name: t('book.pinApprox'),
            address: `${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}`,
          });
        }
        setMapReady(true);
      } catch (e) {
        if (!cancelled) {
          setMapError(isBillingError(e?.message) ? t('book.billingError') : (e?.message || 'Map failed to load'));
        }
      }
    })();
    return () => {
      cancelled = true;
      document.body.classList.remove('rapido-pin-dragging');
      destroyMap(mapInstance.current);
      mapInstance.current = null;
    };
  }, [t]);

  const sheetRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const sheetEl = sheetRef.current;
    const containerEl = containerRef.current;
    if (!sheetEl || !containerEl) return undefined;
    const apply = () => {
      const h = sheetEl.getBoundingClientRect().height;
      containerEl.style.setProperty('--sheet-h', `${Math.round(h)}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(sheetEl);
    return () => ro.disconnect();
  }, [mapReady, pinPlace, pinLoading]);

  useEffect(() => {
    if (!mapReady) return undefined;
    const map = mapInstance.current;
    if (!map) return undefined;

    let timer;
    const lastKey = { current: '' };

    const runGeocode = (lat, lng, { immediate = false } = {}) => {
      const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      if (key === lastKey.current && !immediate) return;
      clearTimeout(timer);

      const ok = isWithinServiceArea(lat, lng);
      setPinValid(ok);
      setPinPlace({
        lat,
        lng,
        name: ok ? t('book.pinApprox') : t('book.pickupOutside'),
        address: ok
          ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
          : t('book.outsideArea', { km: APP.serviceRadiusKm, city: APP.city }),
      });

      if (!ok) {
        setPinLoading(false);
        lastKey.current = key;
        return;
      }

      const fetchPlace = async () => {
        const seq = ++geocodeSeq.current;
        setPinLoading(true);
        lastKey.current = key;
        try {
          const place = await reverseGeocode(lat, lng);
          if (seq !== geocodeSeq.current) return;
          setPinPlace(place);
          setPinValid(true);
        } catch {
          if (seq !== geocodeSeq.current) return;
          setPinPlace({
            lat,
            lng,
            name: t('book.pinApprox'),
            address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
          });
          setPinValid(true);
        } finally {
          if (seq === geocodeSeq.current) setPinLoading(false);
        }
      };

      if (immediate) fetchPlace();
      else timer = setTimeout(fetchPlace, 90);
    };

    const getPinPadding = () => {
      const sheetH = sheetRef.current ? sheetRef.current.getBoundingClientRect().height : 220;
      return { top: 56, bottom: Math.round(sheetH) };
    };

    const onCenterChange = (point, opts = {}) => {
      if (!point) return;
      runGeocode(point.lat, point.lng, opts);
    };

    const unbindPin = bindPinnedCenterPin(map, {
      getPadding: getPinPadding,
      onDragStart: () => document.body.classList.add('rapido-pin-dragging'),
      onDragEnd: () => document.body.classList.remove('rapido-pin-dragging'),
      onCommitCenter: onCenterChange,
    });

    return () => {
      clearTimeout(timer);
      document.body.classList.remove('rapido-pin-dragging');
      unbindPin();
    };
  }, [mapReady, t]);

  const recenter = async () => {
    setLocating(true);
    try {
      const pos = await getBestDevicePosition().catch(() => getAccuratePosition());
      if (mapInstance.current) {
        fitMapToPoint(mapInstance.current, pos, { top: 56, bottom: sheetRef.current?.offsetHeight || 220 }, 17);
      }
      const inArea = isWithinServiceArea(pos.lat, pos.lng);
      setPinValid(inArea);
      setPinPlace({
        lat: pos.lat,
        lng: pos.lng,
        name: inArea ? t('book.pinApprox') : t('book.pickupOutside'),
        address: inArea
          ? `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`
          : t('book.outsideArea', { km: APP.serviceRadiusKm, city: APP.city }),
      });
      if (!inArea) {
        toast.error(t('book.outsideArea', { km: APP.serviceRadiusKm, city: APP.city }));
        setPinLoading(false);
      } else {
        setPinLoading(true);
        try {
          const place = await reverseGeocode(pos.lat, pos.lng);
          setPinPlace(place);
        } catch {
          /* keep GPS */
        } finally {
          setPinLoading(false);
        }
      }
    } catch (e) {
      if (e?.message === 'denied') {
        showLocationDeniedToast(t);
      } else if (!isBillingError(e?.message)) {
        toast.error(t(getGeoErrorKey(e?.message)));
      }
    } finally {
      setLocating(false);
    }
  };

  const confirm = () => {
    if (!pinPlace || !pinValid) return;
    onConfirm({
      name: pinPlace.name || pinPlace.address,
      address: pinPlace.address || pinPlace.name,
      lat: pinPlace.lat,
      lng: pinPlace.lng,
    });
  };

  return (
    <div ref={containerRef} className="place-pin-overlay rapido-step-pin" role="dialog" aria-modal="true" aria-label={title}>
      <div ref={mapRef} className="place-pin-map" />
      <div className="rapido-center-pin" aria-hidden>
        <div className="rapido-center-pin-stem">
          <div className={`rapido-center-pin-head ${pinKind === 'work' ? 'drop' : 'pickup'}`} />
        </div>
        <div className="rapido-center-pin-shadow" />
      </div>

      <button type="button" className="place-pin-back" onClick={onClose} aria-label={t('profile.cancelEdit')}>
        ←
      </button>
      <button type="button" className="rapido-gps-fab place-pin-gps" onClick={recenter} aria-label={t('book.recenter')}>
        {locating ? '…' : <MapRecenterIcon />}
      </button>

      {mapError && <div className="rapido-map-error">{mapError}</div>}

      <div ref={sheetRef} className="rapido-sheet open rapido-sheet-pin place-pin-sheet">
        <div className="rapido-sheet-handle" aria-hidden />
        <h2 className="rapido-sheet-title">{title}</h2>
        <p className="rapido-pin-hint">{t('book.pinHint')}</p>
        <div className={`rapido-pin-addr-card ${!pinValid ? 'invalid' : ''}`}>
          <span className={`rapido-loc-icon ${pinKind === 'work' ? 'drop' : 'pickup'}`} />
          <div className="rapido-pin-addr-text">
            <span className="rapido-pin-addr-name">{pinName}</span>
            {pinAddr && <span className="rapido-pin-addr-sub">{pinAddr}</span>}
          </div>
        </div>
        <button
          type="button"
          className="rapido-book-btn"
          disabled={!pinPlace || !pinValid || pinLoading}
          onClick={confirm}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}
